import type { Ring, Vec2, Vec3 } from '../../../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../../mesh/meshBuilder';
import { SURFACE } from '../../../mesh/attributes';
import { ensureCCW } from '../../../../geo/polygon';
import { insetConvexRing } from '../../planGeometry';
import {
  coneSolid,
  horizontalCap,
  octahedronSolid,
  orientedFace,
  planPoint,
  prismSolid,
  ringPoints,
  triangulateRing,
} from '../../solids';

/**
 * Parts of the gates and towers of the town wall: corbelled bands, merlons, flared tower roofs,
 * finials, window panels and gate blocks with a round-arched passage. Plan points are (u, v) in the
 * frame, heights y; every part is a closed solid, its foot left open where it stands on another.
 */
export interface FortressStyles {
  readonly wall: VertexStyle;
  readonly roof: VertexStyle;
  /** Openings: the painting draws every window and loophole as one dark mark. */
  readonly dark: VertexStyle;
  /** Mouldings, plinths and window surrounds in pale stone, the painting's white lines on the gates. */
  readonly trim: VertexStyle;
}

/** A roof's swatch on the parts that shed no rain, its soffits and finials, shaded as stone. */
export function roofTrim(roof: VertexStyle): VertexStyle {
  return { ...roof, surface: SURFACE.stone };
}

/** A convex ring grown outward by `by` metres (negative: shrunk), counter-clockwise. */
export function grown(ring: Ring, by: number): Ring {
  return insetConvexRing(ensureCCW(ring), -by);
}

export function ringCentre(ring: Ring): Vec2 {
  const sum = ring.reduce((a, p): Vec2 => [a[0] + p[0], a[1] + p[1]], [0, 0]);
  return [sum[0] / ring.length, sum[1] / ring.length];
}

/** The ring between two convex outlines, the outer round the inner, from y0 up to y1, closed. */
export function ringBand(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  rings: { readonly inner: Ring; readonly outer: Ring },
  y: readonly [number, number]
): void {
  const inner = ensureCCW(rings.inner);
  const outer = ensureCCW(rings.outer);
  prismSolid(builder, style, frame, outer, y[0], y[1], { top: false, bottom: false });
  builder.prism(style, frame, [...inner].reverse(), y[0], y[1], {
    top: false,
    bottom: false,
    sides: true,
  });
  for (const tri of triangulateRing(outer, [[...inner].reverse()])) {
    orientedFace(
      builder,
      style,
      frame,
      tri.map((q) => planPoint(q[0], y[1], q[1])),
      [0, 1, 0]
    );
    orientedFace(
      builder,
      style,
      frame,
      tri.map((q) => planPoint(q[0], y[0], q[1])),
      [0, -1, 0]
    );
  }
}

/**
 * The band a corbel table carries round a wall: the ring between a convex wall outline and that
 * outline grown by `reach`, from y0 up to y1. Its underside is the corbel's soffit.
 */
export function corbelBand(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  wall: Ring,
  reach: number,
  y: readonly [number, number]
): void {
  ringBand(builder, style, frame, { inner: wall, outer: grown(wall, reach) }, y);
}

export interface MerlonSpec {
  /** Merlon width along the wall and the distance between merlon centres. */
  readonly width: number;
  readonly spacing: number;
  /** How far each merlon reaches in from the wall line, to the line's left. */
  readonly depth: number;
  readonly y0: number;
  readonly y1: number;
}

/** Merlons on a wall top from `from` to `to`, as many whole bays as fit, the first and last half a bay in. */
export function merlonsAlong(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  line: { readonly from: Vec2; readonly to: Vec2 },
  spec: MerlonSpec
): number {
  const { from, to } = line;
  const len = Math.hypot(to[0] - from[0], to[1] - from[1]);
  const count = Math.floor(len / spec.spacing);
  if (count < 1) return 0;
  const t: Vec2 = [(to[0] - from[0]) / len, (to[1] - from[1]) / len];
  const n: Vec2 = [-t[1], t[0]];
  const bay = len / count;
  const at = (s: number, d: number): Vec2 => [
    from[0] + t[0] * s + n[0] * d,
    from[1] + t[1] * s + n[1] * d,
  ];
  for (let k = 0; k < count; k++) {
    const mid = bay * (k + 0.5);
    const [s0, s1] = [mid - spec.width / 2, mid + spec.width / 2];
    const ring: Ring = [at(s0, 0), at(s1, 0), at(s1, spec.depth), at(s0, spec.depth)];
    prismSolid(builder, style, frame, ring, spec.y0, spec.y1, { top: true, bottom: false });
  }
  return count;
}

export interface RoofKick {
  /** Height of the kick above the eaves as a share of the roof's rise, and the ring's scale there. */
  readonly riseShare: number;
  readonly scale: number;
}

function scaledAbout(ring: Ring, centre: Vec2, k: number): Ring {
  return ring.map((p): Vec2 => [
    centre[0] + (p[0] - centre[0]) * k,
    centre[1] + (p[1] - centre[1]) * k,
  ]);
}

/**
 * A tower roof over a convex ring: a cone or pyramid to `apexY` above the ring's centre, closed
 * underneath. With a kick, the eaves flare: a steeper cone stands on a flatter skirt.
 */
export function towerRoof(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  ring: Ring,
  heights: { readonly eave: number; readonly apex: number },
  kick?: RoofKick
): void {
  const base = ensureCCW(ring);
  const centre = ringCentre(base);
  const top = planPoint(centre[0], heights.apex, centre[1]);
  horizontalCap(builder, roofTrim(style), frame, base, heights.eave, false);
  const upper = kick
    ? {
        ring: scaledAbout(base, centre, kick.scale),
        y: heights.eave + kick.riseShare * (heights.apex - heights.eave),
      }
    : { ring: base, y: heights.eave };
  if (kick)
    skirt(builder, style, frame, { lower: base, upper: upper.ring, centre }, [
      heights.eave,
      upper.y,
    ]);
  upper.ring.forEach((a, i) => {
    const b = upper.ring[(i + 1) % upper.ring.length]!;
    const face = [planPoint(a[0], upper.y, a[1]), planPoint(b[0], upper.y, b[1]), top];
    orientedFace(builder, style, frame, face, outwardUp(a, b, centre));
  });
}

/**
 * A round roof whose outline follows `profile`, rings of [height, radius] rising from the eaves, and runs
 * straight from the last ring to the apex: a bell where the rings curve in. Closed underneath in trim.
 */
export function profileRoof(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  roof: {
    readonly centre: Vec2;
    readonly segments: number;
    readonly profile: readonly (readonly [number, number])[];
    readonly apex: number;
  }
): void {
  const { centre, segments, profile, apex } = roof;
  const rings = profile.map(([y, r]) => ({ y, ring: ringPoints(centre, r, segments, 0) }));
  horizontalCap(builder, roofTrim(style), frame, rings[0]!.ring, rings[0]!.y, false);
  rings.slice(1).forEach((upper, k) => {
    const lower = rings[k]!;
    skirt(builder, style, frame, { lower: lower.ring, upper: upper.ring, centre }, [
      lower.y,
      upper.y,
    ]);
  });
  const last = rings[rings.length - 1]!;
  const top = planPoint(centre[0], apex, centre[1]);
  last.ring.forEach((a, i) => {
    const b = last.ring[(i + 1) % last.ring.length]!;
    const face = [planPoint(a[0], last.y, a[1]), planPoint(b[0], last.y, b[1]), top];
    orientedFace(builder, style, frame, face, outwardUp(a, b, centre));
  });
}

function outwardUp(a: Vec2, b: Vec2, centre: Vec2): Vec3 {
  const mid: Vec2 = [(a[0] + b[0]) / 2 - centre[0], (a[1] + b[1]) / 2 - centre[1]];
  return [mid[0], Math.hypot(mid[0], mid[1]), -mid[1]];
}

function skirt(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  rings: { readonly lower: Ring; readonly upper: Ring; readonly centre: Vec2 },
  y: readonly [number, number]
): void {
  const { lower, upper, centre } = rings;
  lower.forEach((a, i) => {
    const j = (i + 1) % lower.length;
    const b = lower[j]!;
    const face = [
      planPoint(a[0], y[0], a[1]),
      planPoint(b[0], y[0], b[1]),
      planPoint(upper[j]![0], y[1], upper[j]![1]),
      planPoint(upper[i]![0], y[1], upper[i]![1]),
    ];
    orientedFace(builder, style, frame, face, outwardUp(a, b, centre));
  });
}

/** A finial in its roof's trim: a slender six-sided spike from `base` to `top` over `centre`, with a knob on it. */
export function finial(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  centre: Vec2,
  heights: { readonly base: number; readonly top: number },
  radius: number
): void {
  const trim = roofTrim(style);
  const spike = { center: centre, radius, segments: 6, phase: 0 };
  coneSolid(builder, trim, frame, spike, heights.base, heights.top, true);
  const knob = heights.base + 0.6 * (heights.top - heights.base);
  octahedronSolid(builder, trim, frame, planPoint(centre[0], knob, centre[1]), radius * 1.6);
}
